<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useDashboardStore } from '@/stores/dashboard'
import { usePolling } from '@/composables/usePolling'
import SummaryCards from '@/components/dashboard/SummaryCards.vue'
import StationCard from '@/components/dashboard/StationCard.vue'
import StationTableRow from '@/components/dashboard/StationTableRow.vue'
import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
import Pagination from '@/components/common/Pagination.vue'
import { useI18n } from '@/i18n'

const store = useDashboardStore()
const { locale, t } = useI18n()
const searchQuery = ref('')
const viewMode = ref<'grid' | 'list'>('grid')

const { start, isPolling } = usePolling(async () => {
  await store.refresh()
}, 30000)

const filteredStations = computed(() => {
  if (!searchQuery.value) return store.paginatedStations
  const query = searchQuery.value.toLowerCase()
  return store.stations.filter(station =>
    station.station_name.toLowerCase().includes(query) ||
    station.station_code.toLowerCase().includes(query)
  )
})

const displayedCount = computed(() => filteredStations.value.length)
const totalCount = computed(() => store.stations.length)

const handlePageChange = (newPage: number) => {
  store.setPage(newPage)
}

const formatLastUpdated = (date: Date) => {
  const now = new Date()
  const diff = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diff < 60) return t('Just now')
  if (diff < 120) return t('1 min ago')
  if (diff < 3600) return t('{count} min ago', { count: Math.floor(diff / 60) })
  return date.toLocaleTimeString(locale.value === 'th' ? 'th-TH' : 'en-GB', { hour: '2-digit', minute: '2-digit' })
}

onMounted(() => {
  start()
})
</script>

<template>
  <div>
    <!-- Header -->
    <div class="mb-6 flex justify-between items-center">
      <h1 class="text-2xl font-bold text-gray-800">{{ t('MDB Monitoring Dashboard') }}</h1>
      <div class="flex items-center space-x-3">
        <!-- Refresh Indicator -->
        <Transition
          enter-active-class="transition-opacity duration-200"
          enter-from-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="transition-opacity duration-200"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <span v-if="store.refreshing" class="flex items-center text-sm text-blue-500">
            <svg class="w-4 h-4 mr-1.5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ t('Updating...') }}
          </span>
        </Transition>
        <!-- Live Indicator -->
        <span v-if="isPolling && !store.refreshing" class="flex items-center text-sm text-gray-500">
          <span class="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
          {{ t('Live') }}
        </span>
        <!-- Last Updated -->
        <span v-if="store.lastUpdated && !store.refreshing" class="text-xs text-gray-400 hidden sm:inline">
          {{ formatLastUpdated(store.lastUpdated) }}
        </span>
        <!-- Refresh Button -->
        <button
          @click="store.refresh()"
          :disabled="store.refreshing"
          class="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          :title="t('Refresh')"
        >
          <svg
            class="w-5 h-5 transition-transform"
            :class="{ 'animate-spin': store.refreshing }"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Summary Cards (คำนวณจาก stations data) -->
    <SummaryCards
      v-if="store.stations.length"
      :summary="store.computedSummary"
      :lastUpdated="store.lastUpdated"
      :warningStations="store.warningStations"
    />

    <!-- Power Stations Header -->
    <div class="bg-white rounded-lg shadow-md p-4 mb-4">
      <div class="flex flex-wrap items-center justify-between gap-4">
        <!-- Title with count -->
        <div class="flex items-center space-x-3">
          <svg class="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
            <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
          </svg>
          <span class="font-semibold text-gray-800">{{ t('EV Stations') }}</span>
          <span class="text-gray-500">({{ displayedCount }}/{{ totalCount }})</span>
        </div>

        <!-- Search and View Toggle -->
        <div class="flex items-center space-x-3">
          <!-- Search -->
          <div class="relative">
            <svg class="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="t('Search stations...')"
              class="pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-48"
            />
          </div>

          <!-- View Toggle -->
          <div class="flex border border-gray-200 rounded-lg overflow-hidden">
            <button
              @click="viewMode = 'grid'"
              class="p-2 transition-colors"
              :class="viewMode === 'grid' ? 'bg-blue-500 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'"
              :title="t('Grid view')"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              @click="viewMode = 'list'"
              class="p-2 transition-colors"
              :class="viewMode === 'list' ? 'bg-blue-500 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'"
              :title="t('List view')"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clip-rule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="store.loading && !store.stations.length" class="flex justify-center py-12">
      <LoadingSpinner />
    </div>

    <!-- Error State -->
    <div v-else-if="store.error" class="bg-red-50 text-red-600 p-4 rounded-lg">
      {{ store.error }}
    </div>

    <!-- Station Grid View -->
    <div
      v-else-if="viewMode === 'grid'"
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 transition-opacity duration-300"
      :class="{ 'opacity-60': store.refreshing }"
    >
      <StationCard
        v-for="station in filteredStations"
        :key="station.station_id"
        :station="station"
      />
    </div>

    <!-- Station List View (Table) -->
    <div
      v-else
      class="bg-white rounded-lg shadow-md overflow-hidden transition-opacity duration-300"
      :class="{ 'opacity-60': store.refreshing }"
    >
      <!-- overflow-x-auto + min-w: 8 คอลัมน์ไม่พอที่บนจอแคบ ถ้าไม่มีที่ให้เลื่อน
           เบราว์เซอร์จะบีบคอลัมน์เข้าหากันจนตัวหนังสือตัดบรรทัด -->
      <div class="overflow-x-auto">
        <table class="w-full min-w-[980px]">
          <thead class="bg-gray-50 border-b border-gray-200">
            <tr>
              <!-- min-w ให้คอลัมน์ชื่อ ที่เหลือจะได้กว้างตามเนื้อหาพอดี -->
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap min-w-[240px]">
                {{ t('Station') }}
              </th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                {{ t('Status') }}
              </th>
              <th class="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                {{ t('Power') }}
              </th>
              <th class="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                {{ t('Voltage') }}
              </th>
              <th class="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                {{ t('Current') }}
              </th>
              <!-- ย่อชื่อหัวคอลัมน์: "Energy Today" กับ "Last Update" ยาวกว่า
                   ค่าที่อยู่ข้างล่างมาก หัวเลยเป็นตัวกำหนดความกว้างเสียเอง -->
              <th class="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                {{ t('Energy') }}
              </th>
              <th class="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                {{ t('Updated') }}
              </th>
              <th class="px-4 py-3 w-12"></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <StationTableRow
              v-for="station in filteredStations"
              :key="station.station_id"
              :station="station"
            />
          </tbody>
        </table>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="!store.loading && !filteredStations.length && !store.error" class="text-center py-12 bg-white rounded-lg shadow-md">
      <svg class="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <p class="text-gray-500">
        <span v-if="searchQuery">{{ t('No stations matching "{query}"', { query: searchQuery }) }}</span>
        <span v-else>{{ t('No stations found') }}</span>
      </p>
    </div>

    <!-- Pagination -->
    <Pagination
      v-if="store.totalPages > 1 && !searchQuery"
      :currentPage="store.page"
      :totalPages="store.totalPages"
      :total="store.total"
      :perPage="store.perPage"
      @page-change="handlePageChange"
    />
  </div>
</template>
